// Insurance A/R — "Log" and "Recording" links must open a REAL entry in
// ELVA's work › Log (staging fix §7: "Every Log link opens no entry"). The A/R
// mock tries aren't in the Log fixtures, so each try is pointed at a real Log
// entry of the same kind for the same payer (deterministic per claim + try).
import { getLogEntriesFlat } from '@/features/elva-work/store';
import type { LogEntry } from '@/features/elva-work/types';
import type { ArTry } from './types';
import { canonPayerName } from './payers';

type Kind = 'call' | 'portal' | 'claims';

function kindOf(head: string): Kind {
  if (/call/i.test(head)) return 'call';
  if (/portal|status check/i.test(head)) return 'portal';
  return 'claims';
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** The Log's own payer names differ a little from the A/R ones. */
function logNames(payer: string): string[] {
  const p = canonPayerName(payer);
  if (p === 'Delta Dental of Texas') return ['Delta Dental'];
  if (p === 'BlueCross BlueShield of Texas') return ['BCBS of Texas'];
  return [p];
}

let cache: Map<string, LogEntry[]> | null = null;
function pool(kind: Kind, payer: string): LogEntry[] {
  cache ??= new Map();
  const key = `${kind}|${payer}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const all = getLogEntriesFlat().filter((e) => e.kind === kind);
  const names = logNames(payer);
  const byPayer = all.filter((e) => names.some((n) => e.meta.includes(n)));
  const list = byPayer.length > 0 ? byPayer : all;
  cache.set(key, list);
  return list;
}

/** The real Log entry id this try links to. */
export function logEntryIdFor(payer: string, t: ArTry): string {
  const list = pool(kindOf(t.head), payer);
  const e = list[hash(t.logId) % Math.max(1, list.length)];
  return e?.id ?? 'log-call-aetna';
}

export function logHrefFor(payer: string, t: ArTry): string {
  return `/elva-work/log/${logEntryIdFor(payer, t)}`;
}
